import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Loader2, ChevronRight, ChevronLeft, User, FileText, 
  MessageSquare, CheckCircle2, AlertCircle, Upload, X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const TIPOS_DOCUMENTO = [
  { value: 'DNI', label: 'DNI' },
  { value: 'CE', label: 'Carné de Extranjería' },
  { value: 'RUC', label: 'RUC' },
  { value: 'PASAPORTE', label: 'Pasaporte' },
];

// Componente para mostrar error inline debajo del campo
const FieldError = ({ error }) => {
  if (!error) return null;
  return (
    <p className="text-sm text-red-500 mt-1 flex items-center gap-1 animate-fadeIn">
      <AlertCircle className="w-3 h-3" />
      {error}
    </p>
  );
};

const PublicFormPage = ({ empresaCodigoProp }) => {
  const params = useParams();
  const empresaCodigo = empresaCodigoProp || params.empresaCodigo;
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  
  // Estado para errores por campo
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Referencias para scroll a campos con error
  const fieldRefs = useRef({});
  
  const [empresa, setEmpresa] = useState(null);
  const [proyectos, setProyectos] = useState([]);
  const [selectedProyecto, setSelectedProyecto] = useState(null);
  
  // reCAPTCHA
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Fase 1
    tipo_documento: 'DNI',
    numero_documento: '',
    nombre_completo: '',
    domicilio: '',
    correo_electronico: '',
    telefono: '',
    es_menor_edad: false,
    nombre_apoderado: '',
    correo_apoderado: '',
    telefono_apoderado: '',
    domicilio_apoderado: '',
    // Fase 2
    tipo_bien: '',
    proyecto_id: '',
    monto_reclamado: '',
    descripcion_bien: '',
    // Fase 3
    tipo_reclamo: '',
    descripcion_reclamo: '',
    pedido_consumidor: '',
  });

  const [archivos, setArchivos] = useState([]);
  const [cargandoArchivos, setCargandoArchivos] = useState(false);

  // Función para registrar referencia de campo
  const setFieldRef = useCallback((fieldName) => (el) => {
    if (el) {
      fieldRefs.current[fieldName] = el;
    }
  }, []);

  // Función para hacer scroll al primer campo con error
  const scrollToFirstError = useCallback((errors) => {
    const fieldOrder = [
      // Step 1
      'numero_documento', 'nombre_completo', 'correo_electronico', 'nombre_apoderado',
      // Step 2
      'proyecto_id', 'descripcion_bien',
      // Step 3
      'descripcion_reclamo', 'pedido_consumidor', 'recaptcha'
    ];
    
    for (const field of fieldOrder) {
      if (errors[field] && fieldRefs.current[field]) {
        fieldRefs.current[field].scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Enfocar el campo si es posible
        const input = fieldRefs.current[field].querySelector('input, textarea, button');
        if (input) {
          setTimeout(() => input.focus(), 500);
        }
        break;
      }
    }
  }, []);

  // Limpiar error de un campo cuando el usuario empieza a escribir
  const clearFieldError = (fieldName) => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/proyectos/publico/${empresaCodigo}`);
        setEmpresa(response.data.empresa);
        setProyectos(response.data.proyectos);
      } catch (err) {
        setError('No se pudo cargar la información. Verifique que la URL sea correcta.');
        toast.error('Error al cargar el formulario');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [empresaCodigo]);

  // Callback para reCAPTCHA
  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
    if (token) {
      clearFieldError('recaptcha');
    }
  };

  // Callback cuando el reCAPTCHA expira
  const onRecaptchaExpired = () => {
    setRecaptchaToken(null);
  };

  const handleProyectoChange = (proyectoId) => {
    setFormData({ ...formData, proyecto_id: proyectoId });
    const proyecto = proyectos.find(p => p.id === proyectoId);
    setSelectedProyecto(proyecto);
    clearFieldError('proyecto_id');
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setCargandoArchivos(true);
    const validFiles = files.filter(file => {
      // Tipos permitidos: JPG, PNG, GIF, PDF (alineado con backend)
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
      const maxSize = 5 * 1024 * 1024; // 5MB
      
      if (!validTypes.includes(file.type)) {
        toast.error(`Tipo de archivo no permitido: ${file.name}. Use JPG, PNG, GIF o PDF`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`El archivo ${file.name} excede el tamaño máximo de 5MB`);
        return false;
      }
      return true;
    });
    
    if (archivos.length + validFiles.length <= 3) {
      setArchivos([...archivos, ...validFiles]);
    } else {
      toast.error('Máximo 3 archivos permitidos');
    }
    setCargandoArchivos(false);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setArchivos(archivos.filter((_, i) => i !== index));
  };

  // Validaciones anti-datos inconsistentes
  const tieneCaracteresRepetidos = (str, n = 3) => /(.)\1{2,}/.test(str);
  const todosIguales = (str) => str.length > 1 && new Set(str).size === 1;
  const esSecuencial = (str) => {
    const seq = '0123456789';
    return seq.includes(str) || seq.split('').reverse().join('').includes(str);
  };

  const validarNombre = (nombre) => {
    if (!nombre) return 'El nombre completo es obligatorio';
    const limpio = nombre.trim();
    if (tieneCaracteresRepetidos(limpio)) return 'El nombre contiene caracteres repetidos inválidos';
    const palabras = limpio.split(/\s+/).filter(p => p.length > 0);
    if (palabras.length < 2) return 'Ingrese nombre y apellido';
    if (palabras.some(p => p.length < 2)) return 'Cada parte del nombre debe tener al menos 2 caracteres';
    if (!/^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s\-']+$/.test(limpio)) return 'El nombre solo puede contener letras, espacios y guiones';
    return null;
  };

  const validarDocumento = (tipo, numero) => {
    if (!numero) return 'El número de documento es obligatorio';
    const limpio = numero.trim();
    if (tipo === 'DNI') {
      if (!/^\d{8}$/.test(limpio)) return 'El DNI debe tener exactamente 8 dígitos';
      if (todosIguales(limpio)) return 'El número de documento no es válido';
      if (esSecuencial(limpio)) return 'El número de documento no es válido';
    } else if (tipo === 'RUC') {
      if (!/^\d{11}$/.test(limpio)) return 'El RUC debe tener exactamente 11 dígitos';
      if (!/^(10|15|16|17|20)/.test(limpio)) return 'El RUC debe iniciar con 10, 15, 16, 17 o 20';
      if (todosIguales(limpio)) return 'El número de documento no es válido';
    } else if (tipo === 'CE') {
      if (limpio.length < 9 || limpio.length > 20) return 'El CE debe tener entre 9 y 20 caracteres';
      if (!/^[a-zA-Z0-9]+$/.test(limpio)) return 'El CE solo puede contener letras y números';
      if (todosIguales(limpio)) return 'El número de documento no es válido';
    } else if (tipo === 'PASAPORTE') {
      if (limpio.length < 6 || limpio.length > 20) return 'El pasaporte debe tener entre 6 y 20 caracteres';
      if (!/^[a-zA-Z0-9]+$/.test(limpio)) return 'El pasaporte solo puede contener letras y números';
      if (todosIguales(limpio)) return 'El número de documento no es válido';
    }
    return null;
  };

  const validarCorreo = (correo) => {
    if (!correo) return 'El correo electrónico es obligatorio';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo)) return 'Ingrese un correo electrónico válido';
    const parteLocal = correo.split('@')[0];
    if (/(.)\1{3,}/.test(parteLocal)) return 'El correo electrónico no parece ser válido';
    return null;
  };

  const validarTelefono = (telefono) => {
    if (!telefono) return null; // campo opcional
    const soloDigitos = telefono.replace(/[\s\-+]/g, '');
    if (!/^\d+$/.test(soloDigitos)) return 'El teléfono solo puede contener dígitos, +, espacios y guiones';
    if (soloDigitos.length < 7) return 'El teléfono debe tener al menos 7 dígitos';
    if (todosIguales(soloDigitos)) return 'El número de teléfono no es válido';
    return null;
  };

  const validateStep = (stepNum) => {
    const errors = {};
    
    if (stepNum === 1) {
      const errorDoc = validarDocumento(formData.tipo_documento, formData.numero_documento);
      if (errorDoc) errors.numero_documento = errorDoc;

      const errorNombre = validarNombre(formData.nombre_completo);
      if (errorNombre) errors.nombre_completo = errorNombre;

      const errorCorreo = validarCorreo(formData.correo_electronico);
      if (errorCorreo) errors.correo_electronico = errorCorreo;

      const errorTelefono = validarTelefono(formData.telefono);
      if (errorTelefono) errors.telefono = errorTelefono;

      if (formData.es_menor_edad && !formData.nombre_apoderado) {
        errors.nombre_apoderado = 'El nombre del apoderado es obligatorio para menores de edad';
      }
    }
    
    if (stepNum === 2) {
      if (!formData.tipo_bien) {
        errors.tipo_bien = 'Debe seleccionar un tipo de bien';
      }
      if (!formData.proyecto_id) {
        errors.proyecto_id = 'Debe seleccionar un proyecto';
      }
      if (formData.descripcion_bien && formData.descripcion_bien.length > 1000) {
        errors.descripcion_bien = 'La descripción no puede exceder 1000 caracteres';
      }
    }
    
    if (stepNum === 3) {
      if (!formData.tipo_reclamo) {
        errors.tipo_reclamo = 'Debe seleccionar un tipo (Reclamo o Queja)';
      }
      if (!formData.descripcion_reclamo) {
        errors.descripcion_reclamo = 'La descripción del reclamo es obligatoria';
      } else if (formData.descripcion_reclamo.length > 2000) {
        errors.descripcion_reclamo = 'La descripción no puede exceder 2000 caracteres';
      }
      if (!formData.pedido_consumidor) {
        errors.pedido_consumidor = 'El pedido del consumidor es obligatorio';
      } else if (formData.pedido_consumidor.length > 2000) {
        errors.pedido_consumidor = 'El pedido no puede exceder 2000 caracteres';
      }
    }
    
    // Si hay errores, mostrar toast, setear errores y hacer scroll
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      toast.error('Por favor, corrija los campos marcados en rojo', {
        duration: 4000,
      });
      setTimeout(() => scrollToFirstError(errors), 100);
      return false;
    }
    
    setFieldErrors({});
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
      setFieldErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
      toast.success(`Paso ${step} completado`, { duration: 2000 });
    }
  };

  const prevStep = () => {
    setStep(step - 1);
    setFieldErrors({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;
    
    // Validar reCAPTCHA
    if (!recaptchaToken) {
      setFieldErrors({ recaptcha: 'Complete la verificación de seguridad' });
      toast.error('Por favor, complete la verificación de seguridad');
      setTimeout(() => {
        if (fieldRefs.current.recaptcha) {
          fieldRefs.current.recaptcha.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }

    setSubmitting(true);
    setFieldErrors({});

    try {
      // Usar FormData para enviar archivos
      const formDataToSend = new FormData();
      
      // Agregar todos los campos del formulario
      formDataToSend.append('tipo_documento', formData.tipo_documento);
      formDataToSend.append('numero_documento', formData.numero_documento);
      formDataToSend.append('nombre_completo', formData.nombre_completo);
      formDataToSend.append('correo_electronico', formData.correo_electronico);
      formDataToSend.append('proyecto_id', formData.proyecto_id);
      formDataToSend.append('tipo_bien', formData.tipo_bien);
      formDataToSend.append('tipo_reclamo', formData.tipo_reclamo);
      formDataToSend.append('descripcion_reclamo', formData.descripcion_reclamo);
      formDataToSend.append('pedido_consumidor', formData.pedido_consumidor);
      formDataToSend.append('recaptcha_token', recaptchaToken);
      
      // Campos opcionales
      if (formData.domicilio) formDataToSend.append('domicilio', formData.domicilio);
      if (formData.telefono) formDataToSend.append('telefono', formData.telefono);
      formDataToSend.append('es_menor_edad', formData.es_menor_edad);
      if (formData.nombre_apoderado) formDataToSend.append('nombre_apoderado', formData.nombre_apoderado);
      if (formData.correo_apoderado) formDataToSend.append('correo_apoderado', formData.correo_apoderado);
      if (formData.telefono_apoderado) formDataToSend.append('telefono_apoderado', formData.telefono_apoderado);
      if (formData.domicilio_apoderado) formDataToSend.append('domicilio_apoderado', formData.domicilio_apoderado);
      if (formData.monto_reclamado) formDataToSend.append('monto_reclamado', formData.monto_reclamado);
      if (formData.descripcion_bien) formDataToSend.append('descripcion_bien', formData.descripcion_bien);
      
      // Agregar archivos
      archivos.forEach((archivo) => {
        formDataToSend.append('archivos', archivo);
      });

      const response = await axios.post(
        `${API_URL}/api/reclamos/publico/con-archivos`, 
        formDataToSend,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setSuccess(response.data);
      toast.success('¡Reclamo enviado exitosamente!', { duration: 5000 });
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Error al enviar el reclamo';
      toast.error(errorMsg, { duration: 5000 });
      
      // Si el error es de verificación de seguridad, marcarlo
      if (errorMsg.toLowerCase().includes('verificación') || errorMsg.toLowerCase().includes('seguridad')) {
        setFieldErrors({ recaptcha: errorMsg });
        setTimeout(() => {
          if (fieldRefs.current.recaptcha) {
            fieldRefs.current.recaptcha.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      }
      
      // Resetear reCAPTCHA
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a1a2e]" />
      </div>
    );
  }

  if (!empresa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Empresa no encontrada</h2>
            <p className="text-slate-600">La URL ingresada no corresponde a ninguna empresa registrada.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    // Usar nuevo formato de colores o fallback a valores por defecto
    const colores = empresa?.colores_tema || {};
    const primaryColor = colores.primario || '#C9A85C';
    const secondaryColor = colores.secundario || '#C5B8A2';
    const tertiaryColor = colores.terciario || '#5A7A7A';
    
    const logoUrl = empresa?.logo_url ? (
      empresa.logo_url.startsWith('http') || empresa.logo_url.startsWith('data:') 
        ? empresa.logo_url 
        : `${API_URL}${empresa.logo_url}`
    ) : null;
    
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-lg w-full animate-fadeIn">
          <CardContent className="pt-8 pb-8 text-center">
            {/* Logo de la empresa */}
            {logoUrl && (
              <div className="mb-6">
                <img 
                  src={logoUrl} 
                  alt={empresa?.nombre || 'Logo'} 
                  className="h-16 mx-auto object-contain"
                  data-testid="success-logo"
                />
              </div>
            )}
            
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
              Reclamo Registrado
            </h2>
            <p className="text-slate-600 mb-6">
              Su reclamo fue registrado exitosamente con el número de folio:
            </p>
            <div className="bg-slate-100 rounded-lg py-4 px-6 mb-6">
              <span className="font-mono text-2xl font-bold" style={{ color: primaryColor }}>
                {success.folio}
              </span>
            </div>
            <p className="text-sm text-slate-500 mb-8">
              Una copia de este reclamo le llegará a su correo electrónico.
            </p>
            <Button
              onClick={() => navigate(`/reclamos/${empresaCodigo}`)}
              data-testid="volver-inicio-btn"
              className="text-white"
              style={{ backgroundColor: primaryColor }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = secondaryColor; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = primaryColor; }}
            >
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Usar nuevo formato de colores o fallback
  const coloresEmpresa = empresa.colores_tema || {};
  const colores = {
    primario: coloresEmpresa.primario || '#C9A85C',
    secundario: coloresEmpresa.secundario || '#C5B8A2',
    terciario: coloresEmpresa.terciario || coloresEmpresa.acento || '#5A7A7A',
    cuaternario: coloresEmpresa.cuaternario || '#5A7A7A',
    fondo: coloresEmpresa.fondo || '#FFFFFF',
    superficie: coloresEmpresa.superficie || '#F5F5F5',
    fondo_cabecera: coloresEmpresa.fondo_cabecera || '#333333',
    texto: coloresEmpresa.texto || '#333333',
    texto_secundario: coloresEmpresa.texto_secundario || '#666666',
    error: coloresEmpresa.error || '#DC3545'
  };

  // Helper para oscurecer color
  const darkenColor = (hex, percent = 15) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  };

  // Handlers para hover de botones
  const primaryButtonProps = {
    style: { backgroundColor: colores.primario },
    onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = colores.secundario; },
    onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = colores.primario; },
  };

  const accentButtonProps = {
    style: { backgroundColor: colores.terciario },
    onMouseEnter: (e) => { e.currentTarget.style.backgroundColor = colores.primario; },
    onMouseLeave: (e) => { e.currentTarget.style.backgroundColor = colores.terciario; },
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header style={{ backgroundColor: colores.fondo_cabecera }} className="text-white py-6">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-center gap-4">
          {empresa.logo_url && (
            <img src={empresa.logo_url} alt="Logo" className="h-12 w-auto object-contain" />
          )}
          <div className="text-center">
            <h1 className="font-heading text-2xl md:text-3xl font-bold">
              Libro de Reclamaciones Virtual
            </h1>
            <p className="text-white/70 mt-1">{empresa.nombre}</p>
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center mb-8">
          {[1, 2, 3].map((s, index) => (
            <React.Fragment key={s}>
              <div className="flex flex-col items-center">
                <div 
                  className={`wizard-step ${step === s ? 'active' : step > s ? 'completed' : 'inactive'}`}
                  style={step === s ? { backgroundColor: colores.primario, boxShadow: `0 0 0 4px ${colores.primario}33` } : {}}
                >
                  {step > s ? <CheckCircle2 className="w-5 h-5" /> : s}
                </div>
                <span className={`text-xs mt-2 ${step >= s ? 'text-slate-700' : 'text-slate-400'}`}>
                  {s === 1 ? 'Identificación' : s === 2 ? 'Bien Contratado' : 'Detalle'}
                </span>
              </div>
              {index < 2 && (
                <div className={`wizard-connector ${step > s ? 'completed' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="shadow-lg border-slate-200">
          {/* Step 1: Identificación del Reclamante */}
          {step === 1 && (
            <CardContent className="p-6 md:p-8 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colores.primario }}>
                  <User className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold">Identificación del Reclamante</h2>
                  <p className="text-sm text-slate-500">Ingrese sus datos personales</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tipo_documento">Tipo de Documento *</Label>
                  <Select
                    value={formData.tipo_documento}
                    onValueChange={(value) => setFormData({ ...formData, tipo_documento: value })}
                  >
                    <SelectTrigger id="tipo_documento" data-testid="tipo-documento-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIPOS_DOCUMENTO.map((tipo) => (
                        <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2" ref={setFieldRef('numero_documento')}>
                  <Label htmlFor="numero_documento">Número de Documento *</Label>
                  <Input
                    id="numero_documento"
                    data-testid="numero-documento-input"
                    value={formData.numero_documento}
                    onChange={(e) => {
                      setFormData({ ...formData, numero_documento: e.target.value });
                      clearFieldError('numero_documento');
                    }}
                    placeholder="Ingrese su número de documento"
                    className={fieldErrors.numero_documento ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <FieldError error={fieldErrors.numero_documento} />
                </div>

                <div className="space-y-2 md:col-span-2" ref={setFieldRef('nombre_completo')}>
                  <Label htmlFor="nombre_completo">Nombre Completo / Razón Social *</Label>
                  <Input
                    id="nombre_completo"
                    data-testid="nombre-completo-input"
                    value={formData.nombre_completo}
                    onChange={(e) => {
                      setFormData({ ...formData, nombre_completo: e.target.value });
                      clearFieldError('nombre_completo');
                    }}
                    placeholder="Ingrese su nombre completo"
                    className={fieldErrors.nombre_completo ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <FieldError error={fieldErrors.nombre_completo} />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="domicilio">Domicilio</Label>
                  <Input
                    id="domicilio"
                    data-testid="domicilio-input"
                    value={formData.domicilio}
                    onChange={(e) => setFormData({ ...formData, domicilio: e.target.value })}
                    placeholder="Ingrese su domicilio"
                  />
                </div>

                <div className="space-y-2" ref={setFieldRef('correo_electronico')}>
                  <Label htmlFor="correo_electronico">Correo Electrónico *</Label>
                  <Input
                    id="correo_electronico"
                    type="email"
                    data-testid="correo-electronico-input"
                    value={formData.correo_electronico}
                    onChange={(e) => {
                      setFormData({ ...formData, correo_electronico: e.target.value });
                      clearFieldError('correo_electronico');
                    }}
                    placeholder="correo@ejemplo.com"
                    className={fieldErrors.correo_electronico ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <FieldError error={fieldErrors.correo_electronico} />
                </div>

                <div className="space-y-2" ref={el => fieldRefs.current.telefono = el}>
                  <Label htmlFor="telefono">Teléfono</Label>
                  <Input
                    id="telefono"
                    data-testid="telefono-input"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    placeholder="999 999 999"
                    className={fieldErrors.telefono ? 'border-red-500' : ''}
                  />
                  {fieldErrors.telefono && <span className="text-red-500 text-sm">{fieldErrors.telefono}</span>}
                </div>

                <div className="md:col-span-2">
                  <div className="flex items-center space-x-2 mt-4">
                    <Checkbox
                      id="es_menor_edad"
                      data-testid="es-menor-edad-checkbox"
                      checked={formData.es_menor_edad}
                      onCheckedChange={(checked) => setFormData({ ...formData, es_menor_edad: checked })}
                    />
                    <Label htmlFor="es_menor_edad" className="text-sm">
                      Soy menor de edad
                    </Label>
                  </div>
                </div>

                {formData.es_menor_edad && (
                  <>
                    <div className="md:col-span-2 border-t pt-4 mt-2">
                      <h3 className="font-medium text-slate-700 mb-4">Datos del Padre/Madre o Apoderado</h3>
                    </div>
                    <div className="space-y-2 md:col-span-2" ref={setFieldRef('nombre_apoderado')}>
                      <Label htmlFor="nombre_apoderado">Nombre del Apoderado *</Label>
                      <Input
                        id="nombre_apoderado"
                        data-testid="nombre-apoderado-input"
                        value={formData.nombre_apoderado}
                        onChange={(e) => {
                          setFormData({ ...formData, nombre_apoderado: e.target.value });
                          clearFieldError('nombre_apoderado');
                        }}
                        className={fieldErrors.nombre_apoderado ? 'border-red-500 focus:ring-red-500' : ''}
                      />
                      <FieldError error={fieldErrors.nombre_apoderado} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="correo_apoderado">Correo del Apoderado</Label>
                      <Input
                        id="correo_apoderado"
                        type="email"
                        value={formData.correo_apoderado}
                        onChange={(e) => setFormData({ ...formData, correo_apoderado: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="telefono_apoderado">Teléfono del Apoderado</Label>
                      <Input
                        id="telefono_apoderado"
                        value={formData.telefono_apoderado}
                        onChange={(e) => setFormData({ ...formData, telefono_apoderado: e.target.value })}
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-between items-center mt-8">
                <a
                  href={empresaCodigoProp ? '/consulta' : `/consulta/${empresaCodigo}`}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  data-testid="link-consultar-reclamo"
                >
                  Consultar reclamo existente
                </a>
                <Button 
                  onClick={nextStep}
                  data-testid="siguiente-btn-1"
                  className="text-white"
                  {...primaryButtonProps}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          )}

          {/* Step 2: Identificación del Bien Contratado */}
          {step === 2 && (
            <CardContent className="p-6 md:p-8 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colores.primario }}>
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold">Identificación del Bien Contratado</h2>
                  <p className="text-sm text-slate-500">Seleccione el proyecto y detalle el bien</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3" ref={setFieldRef('tipo_bien')}>
                  <Label>Tipo de Bien *</Label>
                  <RadioGroup
                    value={formData.tipo_bien}
                    onValueChange={(value) => setFormData({ ...formData, tipo_bien: value })}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="PRODUCTO" id="producto" data-testid="tipo-bien-producto" />
                      <Label htmlFor="producto">Producto</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="SERVICIO" id="servicio" data-testid="tipo-bien-servicio" />
                      <Label htmlFor="servicio">Servicio</Label>
                    </div>
                  </RadioGroup>
                  <FieldError error={fieldErrors.tipo_bien} />
                </div>

                <div className="space-y-2" ref={setFieldRef('proyecto_id')}>
                  <Label htmlFor="proyecto">Proyecto *</Label>
                  <Select
                    value={formData.proyecto_id}
                    onValueChange={handleProyectoChange}
                  >
                    <SelectTrigger 
                      id="proyecto" 
                      data-testid="proyecto-select"
                      className={fieldErrors.proyecto_id ? 'border-red-500 focus:ring-red-500' : ''}
                    >
                      <SelectValue placeholder="Seleccione un proyecto" />
                    </SelectTrigger>
                    <SelectContent>
                      {proyectos.map((proy) => (
                        <SelectItem key={proy.id} value={proy.id}>{proy.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError error={fieldErrors.proyecto_id} />
                  
                  {/* Información del proyecto seleccionado */}
                  {selectedProyecto && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg animate-fadeIn border border-slate-200 mt-3">
                      <div>
                        <Label className="text-xs text-slate-500">Razón Social</Label>
                        <p className="font-medium text-sm">{selectedProyecto.razon_social}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">RUC</Label>
                        <p className="font-mono text-sm">{selectedProyecto.ruc}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Dirección</Label>
                        <p className="text-sm">{selectedProyecto.direccion}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fecha_reclamo">Fecha del Reclamo</Label>
                    <Input
                      id="fecha_reclamo"
                      value={new Date().toLocaleDateString('es-PE')}
                      disabled
                      className="bg-slate-50"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="monto_reclamado">Monto Reclamado (S/)</Label>
                    <Input
                      id="monto_reclamado"
                      type="number"
                      step="0.01"
                      data-testid="monto-reclamado-input"
                      value={formData.monto_reclamado}
                      onChange={(e) => setFormData({ ...formData, monto_reclamado: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div className="space-y-2" ref={setFieldRef('descripcion_bien')}>
                  <Label htmlFor="descripcion_bien">
                    Descripción del Bien
                    <span className="text-xs text-slate-400 ml-2">
                      ({formData.descripcion_bien.length}/1000)
                    </span>
                  </Label>
                  <Textarea
                    id="descripcion_bien"
                    data-testid="descripcion-bien-textarea"
                    value={formData.descripcion_bien}
                    onChange={(e) => {
                      setFormData({ ...formData, descripcion_bien: e.target.value });
                      clearFieldError('descripcion_bien');
                    }}
                    maxLength={1000}
                    rows={4}
                    placeholder="Describa el bien o servicio contratado"
                    className={fieldErrors.descripcion_bien ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <FieldError error={fieldErrors.descripcion_bien} />
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  onClick={prevStep}
                  data-testid="anterior-btn-2"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Anterior
                </Button>
                <Button 
                  onClick={nextStep}
                  data-testid="siguiente-btn-2"
                  className="text-white"
                  {...primaryButtonProps}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          )}

          {/* Step 3: Detalle del Reclamo */}
          {step === 3 && (
            <CardContent className="p-6 md:p-8 animate-fadeIn">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: colores.primario }}>
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold">Detalle del Reclamo</h2>
                  <p className="text-sm text-slate-500">Describa su reclamo o queja</p>
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-3" ref={setFieldRef('tipo_reclamo')}>
                  <Label>Tipo *</Label>
                  <RadioGroup
                    value={formData.tipo_reclamo}
                    onValueChange={(value) => setFormData({ ...formData, tipo_reclamo: value })}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="RECLAMO" id="reclamo" data-testid="tipo-reclamo" />
                      <Label htmlFor="reclamo">Reclamo</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="QUEJA" id="queja" data-testid="tipo-queja" />
                      <Label htmlFor="queja">Queja</Label>
                    </div>
                  </RadioGroup>
                  <FieldError error={fieldErrors.tipo_reclamo} />
                  <p className="text-xs text-slate-500">
                    <strong>Reclamo:</strong> Disconformidad con los productos o servicios. 
                    <strong className="ml-2">Queja:</strong> Disconformidad no relacionada a los productos o servicios; o, malestar o descontento respecto a la atención al público.
                  </p>
                </div>

                <div className="space-y-2" ref={setFieldRef('descripcion_reclamo')}>
                  <Label htmlFor="descripcion_reclamo">
                    Descripción del Reclamo *
                    <span className="text-xs text-slate-400 ml-2">
                      ({formData.descripcion_reclamo.length}/2000)
                    </span>
                  </Label>
                  <Textarea
                    id="descripcion_reclamo"
                    data-testid="descripcion-reclamo-textarea"
                    value={formData.descripcion_reclamo}
                    onChange={(e) => {
                      setFormData({ ...formData, descripcion_reclamo: e.target.value });
                      clearFieldError('descripcion_reclamo');
                    }}
                    maxLength={2000}
                    rows={5}
                    placeholder="Detalle el motivo de su reclamo o queja"
                    className={fieldErrors.descripcion_reclamo ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <FieldError error={fieldErrors.descripcion_reclamo} />
                </div>

                <div className="space-y-2" ref={setFieldRef('pedido_consumidor')}>
                  <Label htmlFor="pedido_consumidor">
                    Pedido del Consumidor *
                    <span className="text-xs text-slate-400 ml-2">
                      ({formData.pedido_consumidor.length}/2000)
                    </span>
                  </Label>
                  <Textarea
                    id="pedido_consumidor"
                    data-testid="pedido-consumidor-textarea"
                    value={formData.pedido_consumidor}
                    onChange={(e) => {
                      setFormData({ ...formData, pedido_consumidor: e.target.value });
                      clearFieldError('pedido_consumidor');
                    }}
                    maxLength={2000}
                    rows={4}
                    placeholder="Indique qué solución espera de su reclamo"
                    className={fieldErrors.pedido_consumidor ? 'border-red-500 focus:ring-red-500' : ''}
                  />
                  <FieldError error={fieldErrors.pedido_consumidor} />
                </div>

                {/* Archivos adjuntos */}
                <div className="space-y-2">
                  <Label>Archivos Adjuntos (máximo 3, 5MB cada uno)</Label>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-4">
                    <input
                      type="file"
                      id="archivos"
                      multiple
                      accept=".pdf,.jpg,.jpeg,.png,.gif"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={cargandoArchivos}
                    />
                    {cargandoArchivos ? (
                      <div className="flex flex-col items-center py-4">
                        <Loader2 className="w-8 h-8 text-slate-400 animate-spin mb-2" />
                        <span className="text-sm text-slate-600">Cargando archivo...</span>
                      </div>
                    ) : (
                      <label 
                        htmlFor="archivos" 
                        className="flex flex-col items-center cursor-pointer py-4"
                      >
                        <Upload className="w-8 h-8 text-slate-400 mb-2" />
                        <span className="text-sm text-slate-600">
                          Haga clic para seleccionar archivos
                        </span>
                        <span className="text-xs text-slate-400 mt-1">
                          PDF, JPG, PNG o GIF
                        </span>
                      </label>
                    )}
                    
                    {archivos.length > 0 && (
                      <div className="mt-4 space-y-2">
                        {archivos.map((file, index) => (
                          <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                            <span className="text-sm truncate">{file.name}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFile(index)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* reCAPTCHA */}
                <div className="bg-slate-50 p-4 rounded-lg" ref={setFieldRef('recaptcha')}>
                  <Label className="mb-3 block">Verificación de Seguridad *</Label>
                  <div className="flex justify-center">
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={RECAPTCHA_SITE_KEY}
                      onChange={onRecaptchaChange}
                      onExpired={onRecaptchaExpired}
                      data-testid="recaptcha-widget"
                    />
                  </div>
                  <FieldError error={fieldErrors.recaptcha} />
                </div>

                {/* Disclaimer */}
                <div className="border-t pt-6 mt-6 space-y-6">
                  {/* Observaciones */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 text-sm mb-2">
                      OBSERVACIONES Y ACCIONES ADOPTADAS POR EL VENDEDOR
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      <strong>Fecha de comunicación de la respuesta:</strong> Al ser un reclamo virtual, 
                      su caso será derivado al área de atención al cliente, a fin de dar respuesta 
                      dentro del plazo legalmente establecido.
                    </p>
                  </div>

                  {/* Aviso de Privacidad */}
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-semibold text-slate-700 text-sm mb-2">
                      AVISO DE PRIVACIDAD
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      En virtud de la Ley N° 29733 – Ley de Protección de Datos Personales, y su Reglamento, sus datos serán almacenados en el banco de datos "Libro de Reclamaciones" de titularidad del proveedor, con la finalidad de establecer un medio de comunicación para brindarle respuesta a las solicitudes de gestión, quejas, reclamos que usted haya registrado a través de la presente hoja de reclamación, por el plazo de dos años desde la finalización de la gestión requerida para atender el reclamo o queja. Sin sus datos personales, no se podrá realizar la finalidad antes indicada. Para tal fin, se podrá encargar el tratamiento de sus datos a terceros, los cuales se pueden visualizar en <a href="https://www.imagina.pe/politica-de-privacidad/" target="_blank" rel="noopener noreferrer" className="underline font-medium" style={{ color: colores.primario }}>www.imagina.pe/politica-de-privacidad/</a>. Podrá ejercer los derechos contenidos en la Ley N° 29733, su reglamento y/o normas modificatorias, dirigiendo una solicitud al correo electrónico <a href="mailto:consultas@imagina.pe" className="underline font-medium" style={{ color: colores.primario }}>consultas@imagina.pe</a>.
                    </p>
                  </div>

                  {/* Párrafo INDECOPI */}
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-xs text-slate-600 leading-relaxed italic">
                      La formulación del reclamo no impide acudir a otras vías de solución de 
                      controversias ni es requisito previo para interponer una denuncia ante el 
                      INDECOPI. El proveedor debe dar respuesta al reclamo o queja en un plazo no 
                      mayor a quince (15) días hábiles, el cual es improrrogable.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex justify-between mt-8">
                <Button 
                  variant="outline" 
                  onClick={prevStep}
                  data-testid="anterior-btn-3"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Anterior
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="enviar-solicitud-btn"
                  className="text-white"
                  {...accentButtonProps}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar Solicitud'
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t py-6 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-xs text-slate-500">
          <p>{empresa.nombre}</p>
          <p className="mt-1">{empresa.direccion}</p>
        </div>
      </footer>
    </div>
  );
};

export default PublicFormPage;
